/**
 * Applies a resolved {@link QualitySettings} object to the live scene (issue
 * #73). Deliberately a pure mapping over an injected {@link QualityApplyTarget}
 * so the settings→renderer/layer wiring is unit-tested in Node with a mock
 * target, no WebGL context required (mirrors quality.ts's Node-testable split).
 *
 * ANTIALIAS IS INTENTIONALLY ABSENT from the target. MSAA is fixed at WebGL
 * context creation (`new WebGLRenderer({ antialias })`), so it can never change
 * without a full page reload; the segmented control persists the choice and
 * reloads for any switch that crosses the antialias boundary (low ↔ med/high),
 * and boot resolves the persisted choice before constructing the renderer. Every
 * setting here is one the live renderer genuinely allows to change without a
 * reload — a single synchronous pass, never partially applied (a WebGL context
 * loss mid-switch must not leave half the settings in the old tier).
 */
import type { QualitySettings } from './quality';

/**
 * The live-mutable surface a {@link QualitySettings} maps onto. main.ts wires
 * each method to the real renderer / lighting rig / bloom pass / scene layers;
 * tests pass a recording mock. Instance-cap setters drive each layer's existing
 * idempotent reconcile path (never a per-frame rebuild) — see the layer
 * `setInstanceCap`/`setCap` methods.
 */
export interface QualityApplyTarget {
  /** Real-time sun shadows on/off (shadow map pass + per-object cast/receive). */
  setShadowsEnabled(enabled: boolean): void;
  /** Sun shadow map resolution (only meaningful when shadows are on). */
  setShadowMapSize(size: number): void;
  /** Upper bound applied to devicePixelRatio when sizing the drawing buffer. */
  setPixelRatioCap(cap: number): void;
  /** Whether the bloom post pass runs at all. */
  setBloom(enabled: boolean): void;
  /** Global cap on baked-mesh tree instances (issue #58). */
  setTreeMeshCap(cap: number): void;
  /** Global cap on billboard-card woodland tree instances (issue #56). */
  setVegetationCap(cap: number): void;
  /** Global cap on rock/boulder instances (issue #63). */
  setRockCap(cap: number): void;
}

/**
 * Applies every live-mutable field of `settings` to `target` in one synchronous
 * pass. Order is fixed and side-effect-free per call so the same settings always
 * produce the same sequence of target calls (what the unit test asserts). Note
 * `settings.antialias`, `vegetationDensityScale`, `vegetationBillboardOnly` are
 * NOT applied here. antialias is reload-only (see file header). The density /
 * billboard-only levers change how many candidate instances a layer GENERATES,
 * which a live switch cannot change without dropping and regenerating every
 * near-camera patch — a synchronous full rebuild the issue forbids as a stall
 * risk. Instead, main.ts pins generation to the bucket maximum at construction
 * (see the alloc-cap wiring), so the realised-instance CAPS above are the sole
 * live GPU-cost lever within an antialias bucket: medium vs high differ by how
 * many of the (bucket-max-density) candidates are drawn, not by regeneration.
 */
export function applyQualitySettings(target: QualityApplyTarget, settings: QualitySettings): void {
  target.setShadowsEnabled(settings.shadows);
  target.setShadowMapSize(settings.shadowMapSize);
  target.setPixelRatioCap(settings.pixelRatioCap);
  target.setBloom(settings.bloom);
  target.setTreeMeshCap(settings.treeMeshInstanceCap);
  target.setVegetationCap(settings.treeInstanceCap);
  target.setRockCap(settings.rockInstanceCap);
}
